'use client'

import { confirm } from '@repo/ui/Confirm'
import { DeleteIcon } from '@repo/ui/DeleteIcon'
import { Drawer } from '@repo/ui/Drawer'
import { EditIcon } from '@repo/ui/EditIcon'
import { EmptyState } from '@repo/ui/EmptyState'
import { KeyDateForm } from '@repo/key-dates-drawer/KeyDateForm'
import { KeyDateReadState } from '@repo/key-dates-drawer/KeyDateReadState'
import { LargeKeyDateIcon } from '@repo/ui/LargeKeyDateIcon'
import { LoadingState } from '@repo/ui/LoadingState'
import { useKeyDateStore } from '@repo/store/useKeyDateStore'
import { useMenuItemsStore } from '@repo/store/useMenuItemsStore'
import { useState, useEffect, useCallback } from 'react'
import MUIAlert from '@mui/material/Alert'
import MUIIconButton from '@mui/material/IconButton'
import MUIStack from '@mui/material/Stack'
import MUITooltip from '@mui/material/Tooltip'
import MUITypography from '@mui/material/Typography'
import PropTypes from 'prop-types'

export const KeyDatesDrawer = ({ testData }) => {
  const {
    deleteKeyDate,
    getKeyDateExtended,
    keyDatesDrawerIsOpen,
    keyDatesDrawerItemId,
    keyDatesDrawerMode,
    toggleKeyDatesDrawer
  } = useKeyDateStore()
  const { allowedActions } = useMenuItemsStore()
  const allowViewKeyDates = allowedActions?.viewKeyDates?.allowed || false
  const allowEditKeyDates = allowedActions?.editKeyDates?.allowed || false
  const [isFetching, setIsFetching] = useState(false)
  const [keyDate, setKeyDate] = useState(null)
  const [matterIsRestricted, setMatterIsRestricted] = useState(false)

  const closeKeyDatesDrawer = () => {
    toggleKeyDatesDrawer({ keyDatesDrawerIsOpen: false })
  }

  const openEditMode = () => {
    toggleKeyDatesDrawer({ keyDatesDrawerIsOpen: true, keyDatesDrawerItemId, keyDatesDrawerMode: 'edit' })
  }

  const handleDelete = async () => {
    const proceed = await confirm('Are you sure you want to delete this key date?')
    if (proceed) {
      await deleteKeyDate({ keyDateId: keyDatesDrawerItemId })
      closeKeyDatesDrawer()
    }
  }

  const fetchKeyDate = useCallback(async () => {
    try {
      setIsFetching(true)
      setMatterIsRestricted(false)
      const keyDate = await getKeyDateExtended({ id: keyDatesDrawerItemId })
      console.log(`keyDate`, keyDate)
      setKeyDate(keyDate)
      setIsFetching(false)
    } catch (err) {
      if (err?.status === 403) {
        setMatterIsRestricted(true)
      }

      setKeyDate(null)
      setIsFetching(false)
      console.error(err)
    }
  }, [getKeyDateExtended, keyDatesDrawerItemId])

  useEffect(() => {
    if (testData) return

    if (keyDatesDrawerIsOpen && keyDatesDrawerItemId) {
      fetchKeyDate()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keyDatesDrawerIsOpen, getKeyDateExtended, keyDatesDrawerItemId, testData])

  useEffect(() => {
    if (!testData && keyDatesDrawerMode === 'view' && keyDatesDrawerItemId) {
      fetchKeyDate()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keyDatesDrawerMode, testData])

  if (!allowViewKeyDates) {
    return <div data-testid="KeyDatesDrawer" />
  }

  return (
    <div data-testid="KeyDatesDrawer">
      <Drawer
        isOpen={keyDatesDrawerIsOpen}
        onClose={closeKeyDatesDrawer}
        headingNode={
          <MUITypography variant="h5" sx={{ mb: 0, alignItems: 'center', display: 'flex' }}>
            <LargeKeyDateIcon sx={{ width: '36px', height: '36px', mr: 3 }} />
            Key date
          </MUITypography>
        }
      >
        <MUIStack direction="column" gap={'2px'} alignItems="flex-end" sx={{ overflowY: 'auto' }}>
          {isFetching ? <LoadingState sx={{ margin: '0 auto' }} /> : null}

          {!isFetching && matterIsRestricted ? (
            <MUIAlert severity="error" sx={{ m: '0px 16px 16px' }}>
              We cannot display details about this key date, as you are not authorised to access the associated matter.
            </MUIAlert>
          ) : null}

          {!isFetching && !matterIsRestricted && keyDatesDrawerMode === 'create' ? (
            <KeyDateForm keyDate={null} />
          ) : null}

          {!isFetching && !matterIsRestricted && keyDatesDrawerMode === 'edit' ? (
            <KeyDateForm keyDate={keyDate} />
          ) : null}

          {!isFetching && !matterIsRestricted && allowEditKeyDates && keyDate && keyDatesDrawerMode === 'view' ? (
            <MUIStack direction="row" justifyContent="flex-end" gap={1} sx={{ width: '100%', p: '0 20px 20px' }}>
              <MUITooltip title="Edit key date" arrow placement="top">
                <MUIIconButton onClick={openEditMode} sx={{ width: '36px', height: '36px' }}>
                  <EditIcon sx={{ width: '18px', height: '18px' }} />
                </MUIIconButton>
              </MUITooltip>
              <MUITooltip title="Delete key date" arrow placement="top">
                <MUIIconButton onClick={handleDelete} sx={{ width: '36px', height: '36px' }}>
                  <DeleteIcon sx={{ width: '18px', height: '18px' }} />
                </MUIIconButton>
              </MUITooltip>
            </MUIStack>
          ) : null}

          {!isFetching && !matterIsRestricted && keyDatesDrawerMode === 'view' ? (
            <>
              {keyDatesDrawerItemId && keyDate ? (
                <KeyDateReadState keyDate={keyDate} />
              ) : (
                <EmptyState
                  sx={{ mt: 10 }}
                  heading={keyDate?.date || 'No key date found'}
                  text="Add a key date to get started"
                  icon={<LargeKeyDateIcon sx={{ marginBottom: 10, width: '60px', height: '60px' }} />}
                />
              )}
            </>
          ) : null}
        </MUIStack>
      </Drawer>
    </div>
  )
}

KeyDatesDrawer.propTypes = {
  testData: PropTypes.any
}
